import React, { useState, useCallback } from 'react';
import { CloseIcon } from '../UI/Icons';
import styles from './ChipEditor.module.css';

export interface ChipEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  ariaLabel?: string;
  allowRanges?: boolean;
}

function naturalSort(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function dedupeAndSort(items: string[]): string[] {
  return [...new Set(items.map((item) => item.trim()).filter(Boolean))].sort(naturalSort);
}

export function parseChipInput(input: string, allowRanges = true): string[] {
  const tokens = input.split(',').map((t) => t.trim()).filter(Boolean);
  const results: string[] = [];

  for (const token of tokens) {
    if (allowRanges && token.includes('-')) {
      const parts = token.split('-').map((p) => p.trim());
      if (parts.length === 2) {
        const [start, end] = parts;
        const startNum = parseInt(start, 10);
        const endNum = parseInt(end, 10);
        if (
          !Number.isNaN(startNum) &&
          !Number.isNaN(endNum) &&
          String(startNum) === start &&
          String(endNum) === end
        ) {
          const from = Math.min(startNum, endNum);
          const to = Math.max(startNum, endNum);
          for (let i = from; i <= to; i += 1) {
            results.push(String(i));
          }
          continue;
        }
      }
    }

    if (token.length > 0 && token.length <= 32) {
      results.push(token);
    }
  }

  return results;
}

const ChipEditor: React.FC<ChipEditorProps> = ({
  value,
  onChange,
  placeholder = 'Add sections… (e.g. 101-105, 200)',
  ariaLabel = 'Sections',
  allowRanges = true,
}) => {
  const [draft, setDraft] = useState('');

  const commitDraft = useCallback(() => {
    const trimmed = draft.trim();
    if (!trimmed) return;

    const parsed = parseChipInput(trimmed, allowRanges);
    if (parsed.length === 0) return;

    onChange(dedupeAndSort([...value, ...parsed]));
    setDraft('');
  }, [allowRanges, draft, onChange, value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const removeChip = (chip: string) => {
    onChange(value.filter((item) => item !== chip));
  };

  return (
    <div className={styles.editor}>
      <div className={styles.chipList} aria-label={ariaLabel}>
        {value.length === 0 ? (
          <span className={styles.emptyHint}>No sections added yet</span>
        ) : (
          value.map((chip) => (
            <span key={chip} className={styles.chip}>
              {chip}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => removeChip(chip)}
                aria-label={`Remove section ${chip}`}
              >
                <CloseIcon size={12} />
              </button>
            </span>
          ))
        )}
      </div>
      <input
        type="text"
        className={styles.chipInput}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commitDraft}
        placeholder={placeholder}
        aria-label={`${ariaLabel} input`}
      />
      {allowRanges && (
        <p className={styles.hint}>Supports single values, comma lists, and ranges like 101-105</p>
      )}
    </div>
  );
};

export default ChipEditor;
